import React, { useState, useEffect, useCallback } from 'react';
import { Select, Table, Input, Button, Tabs } from 'antd';
import { ArrowLeft } from 'lucide-react';
import Swal from 'sweetalert2';
import moment from 'moment';
import { getAppraisalSummary, searchAppraisalSummary, getBellCurveData } from '../../service/appraisalService';
import BellCurveChart from "../../core/modals/appraisal/BellCurveChart";

const { TabPane } = Tabs;

const AppraisalSummary = () => {
  const [appraisals, setAppraisals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [appraisalName, setAppraisalName] = useState('');
  const [periodName, setPeriodName] = useState('');
  const [employeeName, setEmployeeName] = useState('');
  const [epf_number, setEpf_number] = useState('');
  const [status, setStatus] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 5, total: 0 });
  const [bellCurveData, setBellCurveData] = useState(null);
  const [bellCurveLoading, setBellCurveLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('1');

  const getUserId = () => {
    try {
      const storedUserData = localStorage.getItem('user');
      if (!storedUserData) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'User data not found. Please log in again.',
        });
        return null;
      }
      const user = JSON.parse(storedUserData);
      return user.data._id;
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to retrieve user data.',
      });
      return null;
    }
  };

  const userId = getUserId();

  const fetchAppraisalSummary = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data = await getAppraisalSummary(userId);
      setAppraisals(data || []);
      setPagination((prev) => ({ ...prev, total: data ? data.length : 0 }));
    } catch (error) {
      setAppraisals([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const fetchBellCurveData = useCallback(async () => {
    if (!userId) return;
    setBellCurveLoading(true);
    try {
      const data = await getBellCurveData(userId, {
        appraisalName,
        periodName
      });
      setBellCurveData(data);
    } catch (error) {
      console.error('Error fetching bell curve data:', error);
      setBellCurveData(null);
    } finally {
      setBellCurveLoading(false);
    }
  }, [userId, appraisalName, periodName]);

  useEffect(() => {
    fetchAppraisalSummary();
    fetchBellCurveData();
  }, [fetchAppraisalSummary, fetchBellCurveData]);

  const handleSearch = async () => {
    if (!userId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'User not logged in.',
      });
      return;
    }
    setLoading(true);
    try {
      const records = await searchAppraisalSummary({
        userId,
        appraisalName,
        periodName,
        employeeName,
        epf_number,
        status,
        setLoading,
      });
      setAppraisals(records || []);
      setPagination((prev) => ({ ...prev, total: records ? records.length : 0, current: 1 }));
      
      fetchBellCurveData();
    } catch (error) {
      setAppraisals([]);
      setPagination((prev) => ({ ...prev, total: 0, current: 1 }));
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === '2') {
      fetchBellCurveData();
    }
  };

  const columns = [
    {
      title: 'Appraisal Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => text || 'N/A',
    },
    {
      title: 'Employee',
      dataIndex: 'employee',
      key: 'employee',
      render: (text) => text || 'N/A',
    },
    {
      title: 'Period',
      dataIndex: 'period',
      key: 'period',
      render: (text) => text || 'N/A',
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      render: (text) => text || 'N/A',
    },
    {
      title: 'EPF',
      dataIndex: 'epf_number',
      key: 'epf_number',
      render: (text) => text || 'N/A',
    },
    {
      title: 'Final Score',
      dataIndex: 'final_score',
      key: 'final_score',
      render: (score) => score ? score.toFixed(2) : 'N/A',
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => moment(date).format('YYYY-MM-DD'),
    },
    {
      title: 'Comments',
      dataIndex: 'comments',
      key: 'comments',
      render: (text) => text || 'N/A',
    },
    {
      title: 'Reviewer',
      dataIndex: 'reviewer',
      key: 'reviewer',
      render: (text) => text || 'N/A',
    },
  ];

  if (!userId) {
    return (
      <div className="page-wrapper-lt">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="header-leave-container">
                <div>
                  <ArrowLeft size={32} />
                </div>
                <div className="page-title">
                  <h4>Appraisal Summary</h4>
                  <h6>Please log in to view appraisal summaries</h6>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper-lt">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="header-leave-container">
              <div>
                <ArrowLeft size={32} />
              </div>
              <div className="page-title">
                <h4>Appraisal Summary</h4>
                <h6>View appraisal summaries and performance distribution for your department</h6>
              </div>
            </div>
          </div>
        </div>

        <div className="card table-list-card-grid">
          <div className="card-body" style={{ maxWidth: '100%', marginRight: '20px' }}>
            <div
              className="container mt-3"
              style={{ maxWidth: '100%', marginLeft: '20px', marginRight: '40px' }}
            >
              <div className="row d-flex flex-wrap align-items-start justify-content-between">
                <div className="col-12 col-md-3 text-start">
                  <label className="form-label">Appraisal Name</label>
                  <Input
                    value={appraisalName}
                    onChange={(e) => setAppraisalName(e.target.value)}
                    placeholder="Enter appraisal name"
                    style={{ width: '80%', height: '40px' }}
                  />
                </div>
                <div className="col-12 col-md-3 text-start">
                  <label className="form-label">Period Name</label>
                  <Input
                    value={periodName}
                    onChange={(e) => setPeriodName(e.target.value)}
                    placeholder="Enter period name"
                    style={{ width: '80%', height: '40px' }}
                  />
                </div>
                <div className="col-12 col-md-3 mb-3 text-start">
                  <label className="form-label">Employee</label>
                  <Input
                    value={employeeName}
                    onChange={(e) => setEmployeeName(e.target.value)}
                    placeholder="Enter employee name"
                    style={{ width: '80%', height: '40px' }}
                  />
                </div>
                <div className="col-12 col-md-3 mb-3 text-start">
                  <label className="form-label">EPF</label>
                  <Input
                    value={epf_number}
                    onChange={(e) => setEpf_number(e.target.value)}
                    placeholder="Enter EPF number"
                    style={{ width: '80%', height: '40px' }}
                  />
                </div>
                <div className="col-12 col-md-3 mb-3 text-start">
                  <label className="form-label">Status</label>
                  <Select
                    className="select"
                    options={[
                      { value: 'Draft', label: 'Draft' },
                      { value: 'UnderReview', label: 'Under Review' },
                      { value: 'Completed', label: 'Completed' },
                    ]}
                    placeholder="Select status"
                    value={status}
                    onChange={(value) => setStatus(value)}
                    allowClear
                    showSearch
                    style={{ width: '80%', height: '40px' }}
                  />
                </div>
              </div>
            </div>
            <div
              className="container mt-0"
              style={{ maxWidth: '100%', marginLeft: '20px', marginRight: '40px' }}
            >
              <div className="row d-flex align-items-center justify-content-start">
                <div className="col-12 col-md-2 mb-3 text-start">
                  <Button
                    disabled={loading}
                    type="primary"
                    onClick={handleSearch}
                    style={{ height: '38px', width: '55%' }}
                  >
                    {loading ? 'Searching...' : 'Search'}
                  </Button>
                  {loading && (
                    <div className="spinner">
                      <div className="loading-circle"></div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card table-list-card-grid">
          <div className="card-body">
            <Tabs 
              activeKey={activeTab} 
              onChange={handleTabChange}
              type="card"
            >
              <TabPane tab="Appraisal Summary" key="1">
                <div className="table-top"></div>
                <div className="table-responsive">
                  <Table
                    loading={loading}
                    columns={columns}
                    dataSource={appraisals}
                    rowKey={(record) => record.createdAt + record.employee}
                    pagination={{
                      current: pagination.current,
                      pageSize: pagination.pageSize,
                      pageSizeOptions: [5, 10, 20, 50],
                      total: pagination.total,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total) => `Total ${total} items`,
                    }}
                    onChange={handleTableChange}
                  />
                </div>
              </TabPane>
              
              <TabPane tab="Performance Chart" key="2">
                <BellCurveChart 
                  bellCurveData={bellCurveData} 
                  loading={bellCurveLoading}
                />
              </TabPane>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppraisalSummary;
